import { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAppSelector } from '@/hooks/useAppSelector'
import { useAppDispatch } from '@/hooks/useAppDispatch'
import {
  selectIsAuthenticated,
  selectIsLoading,
  selectIsSessionVerified,
  selectNeedsTenantSelection,
  selectHasNoTenants,
  selectHasPendingSignup,
  selectPendingSignupsLoaded,
  fetchPendingSignups,
} from '@/features/auth/authSlice'
import { selectIsHostTenantPending } from '@/features/auth/hostTenantSlice'
import PageLoader from '@/components/common/PageLoader'

/**
 * Protected route wrapper that requires authentication and tenant selection
 * Redirects to login if user is not authenticated
 * Redirects to tenant selection if authenticated but no tenant selected
 * Redirects to plan selection if authenticated with 0 tenants + a pending signup
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render if authenticated
 * @returns {React.ReactElement}
 */
export default function ProtectedRoute({ children }) {
  const dispatch = useAppDispatch()
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const isLoading = useAppSelector(selectIsLoading)
  const isSessionVerified = useAppSelector(selectIsSessionVerified)
  const needsTenantSelection = useAppSelector(selectNeedsTenantSelection)
  const hasNoTenants = useAppSelector(selectHasNoTenants)
  const hasPendingSignup = useAppSelector(selectHasPendingSignup)
  const pendingSignupsLoaded = useAppSelector(selectPendingSignupsLoaded)
  const isHostTenantPending = useAppSelector(selectIsHostTenantPending)
  const location = useLocation()

  // Discover pending signups for a freshly-verified, tenant-less user so we can
  // route them to plan selection instead of the tenant-select "no access" screen.
  useEffect(() => {
    if (isAuthenticated && hasNoTenants && !pendingSignupsLoaded) {
      dispatch(fetchPendingSignups())
    }
  }, [isAuthenticated, hasNoTenants, pendingSignupsLoaded, dispatch])

  // Show the loader only while a session nobody has vouched for yet is first
  // being checked. `isLoading` is shared by every auth thunk, including the
  // fetchCurrentUser a page dispatches to refresh memberships; swapping the
  // page for a loader then unmounts it, and a page that refreshes on mount
  // (checkout return after "completed") remounts, refreshes again and loops
  // forever. A rehydrated session (isAuthenticated is persisted, the
  // verification is not) keeps its page mounted while /auth/me revalidates;
  // swapping it out made the whole shell mount twice on every hard load.
  if (isLoading && !isAuthenticated && !isSessionVerified) {
    return <PageLoader />
  }

  // Redirect to login if not authenticated
  // Preserve the attempted location for redirect after login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // On a workspace host (riverbend.graphit.io) the host's workspace is being
  // made current (useHostTenant): render nothing tenant-scoped, and don't
  // route to the picker, until it is. Never true on the app host.
  if (isHostTenantPending) {
    return <PageLoader />
  }

  // Pay-during-signup: a verified user with no tenants but an active reservation
  // belongs on the plan-selection page, not tenant-select.
  const signupFlowPaths = ['/signup/select-plan', '/signup/checkout-return']
  const isOnSignupFlow = signupFlowPaths.some(p => location.pathname.startsWith(p))
  if (hasNoTenants) {
    if (!pendingSignupsLoaded) {
      // Wait for the pending-signup lookup before deciding, to avoid a flicker
      // through the tenant-select screen.
      return <PageLoader />
    }
    if (hasPendingSignup && !isOnSignupFlow) {
      // Carry the destination so a finished setup comes back to it (e.g. the
      // Rock link consent page); plan selection drops default landing pages.
      return <Navigate to="/signup/select-plan" state={{ from: location }} replace />
    }
  }

  // Redirect to tenant selection if authenticated but no tenant selected
  // (only for multi-tenant users; single-tenant users are auto-selected)
  // Exclude pages that don't require tenant context. The Rock link consent
  // page has its own organization picker and "Create a new organization"
  // action, so a user with none (or several, none current) must reach it.
  const tenantExemptPaths = [
    '/tenant-select', '/onboarding', '/getting-started', '/trial-expired',
    '/signup/select-plan', '/signup/checkout-return',
    '/organizations/new',
  ]
  // Matched exactly: only the consent page itself, never a future
  // /integrations/link* route that expects a current tenant.
  const tenantExemptExactPaths = ['/integrations/link', '/integrations/link/']
  const isTenantExempt = tenantExemptPaths.some(p => location.pathname.startsWith(p))
    || tenantExemptExactPaths.includes(location.pathname)
  if (needsTenantSelection && !isTenantExempt) {
    return <Navigate to="/tenant-select" state={{ from: location }} replace />
  }

  // User is authenticated with tenant context, render protected content
  return children
}
